import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getCertificationAcronym } from '../constants/certifications';
import { 
  MapPin, 
  Calendar, 
  DollarSign, 
  Users, 
  Award, 
  Languages,
  Laptop,
  ExternalLink,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language to flag emoji mapping
const LANGUAGE_FLAGS = {
  'English': '🇬🇧', 'French': '🇫🇷', 'German': '🇩🇪', 'Spanish': '🇪🇸',
  'Italian': '🇮🇹', 'Portuguese': '🇵🇹', 'Dutch': '🇳🇱', 'Russian': '🇷🇺',
  'Chinese (Mandarin)': '🇨🇳', 'Japanese': '🇯🇵', 'Korean': '🇰🇷', 'Arabic': '🇸🇦',
  'Hindi': '🇮🇳', 'Polish': '🇵🇱', 'Swedish': '🇸🇪', 'Danish': '🇩🇰',
  'Norwegian': '🇳🇴', 'Finnish': '🇫🇮', 'Turkish': '🇹🇷', 'Greek': '🇬🇷'
};

const PublicProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/projects/${projectId}`);
      setProject(response.data.project);
      setCompany(response.data.company);
    } catch (error) {
      setError(error.response?.data?.detail || 'Project not found');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = async () => {
    // Track the action
    try {
      await axios.post(`${BACKEND_URL}/api/public/projects/${projectId}/track`, { action: 'apply_click' });
    } catch (e) {
      console.error('Failed to track action');
    }
    // Redirect to signup/login
    navigate('/auth?redirect=/projects');
  };

  const handleContactPM = async () => {
    // Track the action
    try {
      await axios.post(`${BACKEND_URL}/api/public/projects/${projectId}/track`, { action: 'contact_click' });
    } catch (e) {
      console.error('Failed to track action');
    }

    // If user is logged in and is RM, create conversation
    if (user && user.role === 'resource_manager') {
      setIsCreatingConversation(true);
      try {
        const token = localStorage.getItem('token');
        
        // Create a new message thread with the PM's company
        const messageData = {
          to_company_id: project.company_id,
          subject: `Interest in project: ${project.title}`,
          content: `Hello,\n\nI am interested in the project "${project.title}" (${project.project_id}).\n\nI would like to discuss the opportunity further.\n\nBest regards`
        };
        
        await axios.post(`${API}/messages`, messageData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Message sent! Redirecting to your messages...');
        setTimeout(() => navigate('/messages'), 1500);
      } catch (error) {
        toast.error('Failed to send message. Please try again.');
        console.error('Message creation error:', error);
      } finally {
        setIsCreatingConversation(false);
      }
    } else {
      // Redirect to signup/login
      navigate('/auth?redirect=/messages');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-snow">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with logo and branding */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {company?.logo_url && (
              <img src={company.logo_url} alt={company.name} className="h-12 w-12 object-contain" />
            )}
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#1B3240' }}>Snow Exchange</h1>
              <p className="text-sm text-gray-600">{company?.name || 'Professional Opportunity'}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/')} variant="outline">
            Browse All Projects
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* CTA Banner */}
        <Card className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-2" style={{ borderColor: '#62D84E' }}>
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#1B3240' }}>
                  Interested in this opportunity?
                </h2>
                <p className="text-gray-700 mb-4">
                  Join Snow Exchange to apply for this project and connect with top companies in the ServiceNow ecosystem.
                </p>
                <div className="flex gap-3">
                  <Button onClick={handleApplyClick} className="btn-snow" size="lg">
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Apply Now
                  </Button>
                  <Button 
                    onClick={handleContactPM} 
                    variant="outline" 
                    size="lg"
                    disabled={isCreatingConversation}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    {isCreatingConversation ? 'Sending...' : 'Contact Project Manager'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Project Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-mono rounded">
              {project.project_id}
            </span>
            <Badge style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}>
              {project.project_type}
            </Badge>
            <Badge className="bg-green-100 text-green-700">
              🟢 Active
            </Badge>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#1B3240' }}>
            {project.title}
          </h1>
          <p className="text-lg text-gray-700">{project.description}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            
            {/* Skills & Certifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Skills & Certifications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Required Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {project.required_skills?.map((skill, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                {project.required_certifications && project.required_certifications.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">ServiceNow Certifications</p>
                    <div className="flex flex-wrap gap-2">
                      {project.required_certifications.map((cert, idx) => (
                        <Badge 
                          key={idx} 
                          style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}
                          title={cert}
                        >
                          {getCertificationAcronym(cert)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {project.other_certifications && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Other Certifications</p>
                    <p className="text-sm text-gray-600">{project.other_certifications}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Languages */}
            {project.languages_required && project.languages_required.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Languages className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    Language Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {project.languages_required.map((lang, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg"
                      >
                        <span className="text-2xl">
                          {LANGUAGE_FLAGS[lang.language] || '🌐'}
                        </span>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#1B3240' }}>
                            {lang.language}
                          </p>
                          <Badge 
                            className="text-xs mt-1" 
                            style={{ background: 'rgba(98, 216, 78, 0.1)', color: '#62D84E' }}
                          >
                            {lang.level}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Location & Work Arrangement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Location & Work Arrangement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Location</p>
                  <p className="text-gray-900">{project.city}, {project.country}</p>
                </div>

                {project.remote_possible && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Remote Work</p>
                    <Badge className="bg-blue-100 text-blue-700">
                      {project.remote_percentage}% Remote
                    </Badge>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Equipment</p>
                  <Badge className={project.equipment_provided ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {project.equipment_provided ? '✓ Provided by client' : '✗ Consultant provides own'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Budget */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Budget
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {project.price_amount && project.currency && project.price_type ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Rate</p>
                      <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                        {project.currency === 'EUR' ? '€' : project.currency === 'USD' ? '$' : project.currency === 'GBP' ? '£' : project.currency}
                        {project.price_amount.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">per {project.price_type}</p>
                    </div>
                  </>
                ) : project.budget && project.budget_type ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Budget</p>
                    <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                      €{project.budget.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">{project.budget_type}</p>
                  </div>
                ) : (
                  <p className="text-gray-500">To be discussed</p>
                )}

                <div className="pt-3 border-t">
                  <p className="text-sm text-gray-600 mb-1">Commercial Model</p>
                  <Badge className="bg-purple-100 text-purple-700">
                    {project.commercial_model}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {project.duration_value && project.duration_unit && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Duration</p>
                    <p className="text-lg font-semibold" style={{ color: '#1B3240' }}>
                      {project.duration_value} {project.duration_unit}
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-600 mb-1">Start Date</p>
                  <p className="text-gray-900">
                    {new Date(project.start_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Team Requirements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                  Team Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Consultants Needed</p>
                  <p className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                    {project.number_of_consultants}
                  </p>
                </div>

                {project.occupation_rate && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-600 mb-1">Occupation Rate</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {project.occupation_rate}%
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-green-600 to-blue-600 text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">Ready to Apply?</h3>
                <p className="text-sm mb-4 text-green-50">
                  Join thousands of ServiceNow professionals finding their next opportunity.
                </p>
                <Button onClick={handleApplyClick} className="w-full bg-white text-green-700 hover:bg-gray-100">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Create Free Account
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white mt-16 border-t">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-gray-600">
          <p>© 2025 Snow Exchange. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicProjectPage;
